import React from 'react';
import { View, Text, Image, Horizontal, Center, Input } from 'app-studio';
import { UploadIcon } from '../../Icon/Icon';
import { UploadStateProps, UploadViewProps } from './Uploader.props';
// Defines the `UploadView` functional component, responsible for rendering the UI of the Uploader based on provided properties and internal state.
export const UploadView: React.FC<UploadViewProps & UploadStateProps> = ({
  accept,
  isLoading = false,
  progress = 0,
  icon,
  text,
  previewUrl,
  thumbnailUrl,
  errorMessage,
  fileInputRef,
  videoRef,
  selectedFile,
  selectedFiles,
  handleFileChange,
  handleClick,
  multiple = false,
  containerProps,
  errorMessageProps,
  progressProps,
  videoProps,
  imageProps,
  iconProps,
  textProps,
  fileType,
  previewUrl: externalPreviewUrl,
  // Renders an error message with customizable styling when an upload fails or an issue occurs.
  renderError = ({
    errorMessage,
    errorMessageProps,
    themeMode: elementMode,
  }) => (
    <Text
      color="red"
      fontSize={12}
      marginTop={8}
      {...errorMessageProps}
      {...views?.text}
    >
      {errorMessage}
    </Text>
  ),
  renderText = ({ text, textProps }) => (
    <Text marginTop={8} {...textProps} {...views?.text}>
      {text}
    </Text>
  ),
  renderFile = ({ selectedFile, textProps }) => {
    return selectedFile ? (
      <Center
        marginTop={8}
        gap={10}
        flexDirection="column"
        {...views?.container}
      >
        <Text maxLines={2} {...textProps} {...views?.text}>
          {selectedFile.name}
        </Text>
        <Text {...textProps} {...views?.text}>
          ({Math.round(selectedFile.size / 1024)} KB)
        </Text>
      </Center>
    ) : null;
  },
  renderVideo = ({ selectedFile, videoRef, videoProps }) => {
    return (
      <View width="100%" height="100%" position="relative" {...views?.view}>
        <View
          as="video"
          width="100%"
          height="100%"
          src={selectedFile ? URL.createObjectURL(selectedFile) : undefined}
          style={{ objectFit: 'cover' }}
          ref={videoRef}
          {...videoProps}
          {...views?.view}
        />
      </View>
    );
  },
  renderImage = ({ previewUrl, imageProps }) => {
    console.log('Rendering image with URL:', previewUrl);
    return (
      <View width="100%" height="100%" position="relative" {...views?.view}>
        <Image
          src={previewUrl}
          alt="Preview"
          width="100%"
          height="100%"
          objectFit="cover"
          style={{ maxHeight: '100%', maxWidth: '100%' }}
          {...imageProps}
          {...views?.image}
        />
      </View>
    );
  },
  renderProgress = ({ progress, progressProps }) => {
    return (
      <Horizontal
        gap={8}
        alignItems="center"
        {...progressProps}
        {...views?.horizontal}
      >
        <View
          height={4}
          backgroundColor="rgba(0,0,0,0.1)"
          width="100%"
          borderRadius={2}
          {...views?.view}
        >
          <View
            height={4}
            width={`${progress}%`}
            borderRadius={2}
            backgroundColor="color-dark-50"
            {...views?.view}
          />
        </View>
        <Text fontSize={12} {...views?.text}>
          {progress}%
        </Text>
      </Horizontal>
    );
  },
  views = {},
}) => {
  const finalPreviewUrl = externalPreviewUrl || previewUrl;
  return (
    <Center
      onClick={handleClick}
      cursor="pointer"
      border="1px solid rgba(0,0,0,0.1)"
      borderRadius={8}
      padding={16}
      flexDirection="column"
      overflow="hidden"
      position="relative"
      height={selectedFile && progress === 100 ? '200px' : 'auto'}
      {...containerProps}
      {...views?.container}
    >
      {}
      {progress === 100 &&
        fileType === 'image' &&
        finalPreviewUrl &&
        renderImage({ previewUrl: finalPreviewUrl, imageProps })}
      {}
      {progress === 100 &&
        fileType === 'video' &&
        thumbnailUrl &&
        renderVideo({
          selectedFile,
          thumbnailUrl,
          videoRef,
          videoProps,
          imageProps,
        })}
      {}
      {progress === 100 &&
        fileType === 'file' &&
        selectedFile &&
        renderFile({ selectedFile, textProps })}
      {progress < 100 &&
        (icon || <UploadIcon widthHeight={32} {...iconProps} />)}
      {!isLoading && !selectedFile && text && renderText({ text, textProps })}
      {isLoading && renderProgress({ progress, progressProps })}
      {!isLoading &&
        errorMessage &&
        renderError({ errorMessage, errorMessageProps })}
      <Input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        {...views?.view}
      />
    </Center>
  );
};
